import { LitElement, html, css, nothing, type PropertyValues } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';
import { generateUid } from '../../utils/uid';
import { MOBILE_BREAKPOINT_PX } from '../../utils/breakpoints';

type IconVariant = 'small' | 'none';

/**
 * A navigation item used inside `asc-navbar`. Adapts its panel behaviour based
 * on container width: ≥ 1024 px renders a Popover API–anchored dropdown;
 * < 1024 px renders an inline accordion. When the `panel` slot is empty the
 * item behaves as a plain link trigger.
 *
 * @element asc-nav-item
 *
 * @fires asc-toggle - Fired on user-initiated panel open/close. Detail: `{ open: boolean }`
 * @fires asc-click  - Fired when the trigger is activated (both link and plain modes). No detail.
 *
 * @prop {string} href   - When set, the trigger renders as an `<a>` element. Has no effect if the `panel` slot is populated.
 * @prop {string} target - Forwarded to the `<a>` element's `target` attribute. Requires `href`.
 * @prop {string} rel    - Forwarded to the `<a>` element's `rel` attribute. Requires `href`. When `target="_blank"` and `rel` is omitted, defaults to `"noopener noreferrer"`.
 *
 * @slot       - The navigation label text. Required.
 * @slot panel - Optional panel content (dropdown/accordion body).
 * @slot icon  - Optional 20 × 20 SVG displayed to the left of the label; mobile layout only.
 *
 * @csspart base         - Root wrapper div.
 * @csspart trigger      - The trigger element — an `<a>` when `href` is set (link mode) or a `<button>` otherwise.
 * @csspart label        - Span wrapping the default slot.
 * @csspart chevron      - Rotating chevron SVG; always on the far right.
 * @csspart panel        - Panel container (dropdown or accordion).
 * @csspart icon-wrapper - 20 × 20 mobile icon container.
 *
 * @cssprop --asc-nav-item-padding-x                    - Desktop trigger padding-x. Default: `var(--asc-spacing-sm)` (6 px)
 * @cssprop --asc-nav-item-padding-y                    - Desktop trigger padding-y. Default: `var(--asc-spacing-xs)` (4 px)
 * @cssprop --asc-nav-item-gap                          - Desktop gap between label-pad and chevron. Default: `var(--asc-spacing-xxs)` (2 px)
 * @cssprop --asc-nav-item-label-padding-x              - Desktop extra padding around label span. Default: `var(--asc-spacing-xxs)` (2 px)
 * @cssprop --asc-nav-item-border-radius                - Desktop trigger border radius. Default: `var(--asc-radius-md)` (12 px)
 * @cssprop --asc-nav-item-label-color                  - Desktop label text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-nav-item-label-color-hover            - Desktop label text color on hover. Default: `var(--asc-text-secondary_hover)`
 * @cssprop --asc-nav-item-bg-hover                     - Trigger background fill on hover. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-nav-item-mobile-padding-x             - Mobile trigger padding-x. Default: `var(--asc-container-padding-mobile)` (16 px)
 * @cssprop --asc-nav-item-mobile-padding-y             - Mobile trigger padding-y. Default: `var(--asc-spacing-lg)` (12 px)
 * @cssprop --asc-nav-item-mobile-gap                   - Mobile gap between icon, label, chevron. Default: `var(--asc-spacing-lg)` (12 px)
 * @cssprop --asc-nav-item-mobile-label-color           - Mobile label text color. Default: `var(--asc-text-primary)`
 * @cssprop --asc-nav-item-mobile-label-color-hover     - Mobile label text color on hover. Default: `var(--asc-text-primary)`
 * @cssprop --asc-nav-item-chevron-color                - Chevron fill color. Default: `var(--asc-fg-secondary)`
 * @cssprop --asc-nav-item-chevron-transition           - Chevron rotation transition duration. Default: `200ms`
 * @cssprop --asc-nav-item-icon-size          - Small icon wrapper size (mobile). Default: `20px`
 * @cssprop --asc-nav-item-icon-top-offset    - Small icon top alignment offset (mobile). Default: `var(--asc-spacing-xxs)` (2 px)
 * @cssprop --asc-nav-item-panel-border                  - Panel border color (navbar mode). Default: `var(--asc-border-secondary)`
 * @cssprop --asc-nav-item-panel-bg                     - Desktop floating panel background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-nav-item-panel-shadow                 - Desktop floating panel shadow. Default: multi-layer `shadow-lg`
 * @cssprop --asc-nav-item-panel-border-radius          - Desktop floating panel border radius. Default: `var(--asc-radius-xl)`
 * @cssprop --asc-nav-item-panel-z-index                - Desktop floating panel z-index. Default: `100`
 */
@customElement('asc-nav-item')
export class AscNavItem extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    /* ── Base wrapper ─────────────────────────────────────────── */
    .base {
      display: block;
      position: relative;
    }

    /* ── Trigger ──────────────────────────────────────────────── */
    .trigger {
      display: flex;
      align-items: center;
      gap: var(--asc-nav-item-gap, var(--asc-spacing-xxs));
      padding: var(--asc-nav-item-padding-y, var(--asc-spacing-xs))
               var(--asc-nav-item-padding-x, var(--asc-spacing-sm));
      background: transparent;
      border: none;
      border-radius: var(--asc-nav-item-border-radius, var(--asc-radius-md));
      overflow: hidden; /* clip hover bg to rounded corners per Figma overflow-clip */
      cursor: pointer;
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-md);
      font-style: normal;
      font-weight: var(--asc-font-weight-medium);
      line-height: var(--asc-line-height-text-md);
      color: var(--asc-nav-item-label-color, var(--asc-text-secondary));
      text-align: start;
      /* Desktop: width shrinks to text + padding. Mobile override sets width: 100%. */
    }

    .trigger:hover {
      background-color: var(--asc-nav-item-bg-hover, var(--asc-bg-primary_hover));
      color: var(--asc-nav-item-label-color-hover, var(--asc-text-secondary_hover));
    }

    /* Reset anchor defaults when trigger renders as <a> */
    a.trigger {
      text-decoration: none;
    }

    .trigger:focus-visible {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }

    /* ── Label span ───────────────────────────────────────────── */
    .label {
      flex: 1;
      padding-inline: var(--asc-nav-item-label-padding-x, var(--asc-spacing-xxs));
      white-space: nowrap;
    }

    /* ── Chevron ──────────────────────────────────────────────── */
    .chevron {
      display: block;
      width: var(--asc-nav-item-chevron-container-width, 9.999px); /* no-token: Figma intrinsic size */
      height: var(--asc-nav-item-chevron-container-height, 4.665px); /* no-token: Figma intrinsic size */
      padding-inline: var(--asc-nav-item-chevron-padding-x, 4px); /* no-token: fixed Figma size */
      color: var(--asc-nav-item-chevron-color, var(--asc-fg-secondary));
      flex-shrink: 0;
      overflow: visible;
      transform: rotate(180deg);
      transition: transform var(--asc-nav-item-chevron-transition, 200ms) ease;
    }

    :host([open]) .chevron {
      transform: rotate(0deg);
    }

    /* ── Icon wrapper (hidden on desktop, shown mobile-only) ───── */
    .icon-wrapper {
      display: none;
      flex-shrink: 0;
    }

    /* ── Panel container (desktop: popover or absolute; mobile: accordion) ── */
    .panel {
      background-color: var(--asc-nav-item-panel-bg, var(--asc-bg-primary));
      overflow: hidden;
      padding: 0; /* override browser UA [popover] default of 0.25em */
    }

    /*
     * Desktop: all items with a panel use the Popover API so the panel escapes
     * the shadow DOM to the top layer. Coordinates are always injected by
     * _positionDropdown() — never hardcoded here.
     *
     * [standalone] (desktop, outside asc-navbar) → rounded corners.
     * Inside asc-navbar → full-width strip, no radius.
     */
    .panel:popover-open {
      inset: unset;
      margin: 0;
      padding: 0;
      border: none;
      width: auto;
      height: auto;
      max-width: none;
      max-height: none;
      z-index: var(--asc-nav-item-panel-z-index, 100);
      background-color: var(--asc-nav-item-panel-bg, var(--asc-bg-primary));
      box-shadow: var(
        --asc-nav-item-panel-shadow,
        0 12px 16px -4px var(--asc-shadow-lg_01),
        0 4px 6px -2px var(--asc-shadow-lg_02),
        0 2px 2px -1px var(--asc-shadow-lg_03)
      );
      overflow: hidden;
      /* Default: 3-col navbar mode — full-width strip, no border, no radius. */
      border-radius: 0;
      border: none;
    }

    /* Standalone desktop: full border + rounded corners. */
    :host([standalone]) .panel:popover-open {
      border: 1px solid var(--asc-nav-item-panel-border, var(--asc-border-secondary));
      border-radius: var(--asc-nav-item-panel-border-radius, var(--asc-radius-xl));
    }

    /*
     * Floating-2-col navbar mode: full border + 16px radius all around + gap
     * from navbar pill. The gap is applied as a top offset in _positionDropdown().
     */
    .panel[data-mode='floating']:popover-open {
      border: 1px solid var(--asc-nav-item-panel-border, var(--asc-border-secondary));
      border-radius: var(--asc-nav-item-panel-border-radius, var(--asc-radius-lg));
    }

    /* Mobile accordion: panel has no popover attribute. */
    .panel:not([popover]) {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows var(--asc-nav-item-accordion-transition, 300ms) ease-out;
      box-shadow: none;
      border-radius: 0;
    }

    :host([open]) .panel:not([popover]) {
      grid-template-rows: 1fr;
    }

    .panel-inner {
      overflow: hidden;
    }

    /* ── Mobile layout: @container asc-navbar ──────────────────── */
    /* Primary mechanism — fires in browsers where container queries cross shadow boundaries.
     * 1023px is MOBILE_BREAKPOINT_PX (utils/breakpoints.ts) written as a max-width. A
     * container query condition cannot read a custom property, so this literal has to be
     * updated by hand whenever that constant moves. */
    @container asc-navbar (max-width: 1023px) {
      .trigger {
        width: 100%;
        gap: var(--asc-nav-item-mobile-gap, var(--asc-spacing-lg));
        padding: var(--asc-nav-item-mobile-padding-y, var(--asc-spacing-lg))
                 var(--asc-nav-item-mobile-padding-x, var(--asc-container-padding-mobile));
        border-radius: 0;
        color: var(--asc-nav-item-mobile-label-color, var(--asc-text-primary));
      }

      .trigger:hover {
        color: var(--asc-nav-item-mobile-label-color-hover, var(--asc-text-primary));
        background-color: var(--asc-nav-item-mobile-bg-hover, var(--asc-bg-primary_hover));
      }

      .label {
        padding-inline: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* Show icon slot content on mobile */
      .icon-wrapper:not([hidden]) {
        display: flex;
        align-items: flex-start;
        width: var(--asc-nav-item-icon-size, 20px);
        height: var(--asc-nav-item-icon-size, 20px);
        flex-shrink: 0;
        padding-top: var(--asc-nav-item-icon-top-offset, var(--asc-spacing-xxs));
      }
    }

    /* ── Mobile layout: [mobile] attribute fallback ────────────────
     * Mirrors the container query above. Activated by the ResizeObserver which
     * sets [mobile] when the navbar is < 1024 px wide. Ensures correct layout
     * even when @container queries don't traverse the slotting boundary.
     * ─────────────────────────────────────────────────────────────── */
    :host([mobile]) .trigger {
      width: 100%;
      gap: var(--asc-nav-item-mobile-gap, var(--asc-spacing-lg));
      padding: var(--asc-nav-item-mobile-padding-y, var(--asc-spacing-lg))
               var(--asc-nav-item-mobile-padding-x, var(--asc-container-padding-mobile));
      border-radius: 0;
      color: var(--asc-nav-item-mobile-label-color, var(--asc-text-primary));
    }

    :host([mobile]) .trigger:hover {
      color: var(--asc-nav-item-mobile-label-color-hover, var(--asc-text-primary));
      background-color: var(--asc-nav-item-mobile-bg-hover, var(--asc-bg-primary_hover));
    }

    :host([mobile]) .label {
      padding-inline: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    :host([mobile]) .icon-wrapper:not([hidden]) {
      display: flex;
      align-items: flex-start;
      width: var(--asc-nav-item-icon-size, 20px);
      height: var(--asc-nav-item-icon-size, 20px);
      flex-shrink: 0;
      padding-top: var(--asc-nav-item-icon-top-offset, var(--asc-spacing-xxs));
    }
  `;

  /** Whether the panel is currently expanded. Reflects to attribute for `:host([open])` selectors. */
  @property({ type: Boolean, reflect: true }) open = false;

  /** Accessible name forwarded to the trigger's `aria-label`. When omitted, slot text is used. */
  @property({ type: String }) label: string | undefined = undefined;

  /** Navbar type propagated by `asc-navbar` for layout-specific behaviour. */
  @property({ type: String, reflect: true }) type: '3-col' | 'floating-2-col' = '3-col';

  /** Whether this item is currently slotted inside an `asc-navbar`. */
  @property({ type: Boolean, reflect: true, attribute: 'in-navbar' }) inNavbar = false;

  /** Mobile context propagated by `asc-navbar` when the parent navbar is under 1024 px. */
  @property({ type: Boolean, reflect: true }) mobile = false;

  /** When set (and no `panel` slot content exists), the trigger renders as an `<a>` element. */
  @property({ type: String, reflect: true }) href: string | undefined = undefined;

  /** `target` attribute forwarded to the `<a>` element. Only used when `href` is set. */
  @property({ type: String, reflect: true }) target: string | undefined = undefined;

  /** `rel` attribute forwarded to the `<a>` element. Only used when `href` is set. Defaults to `"noopener noreferrer"` when `target="_blank"` and no value is provided. */
  @property({ type: String, reflect: true }) rel: string | undefined = undefined;

  @state() private _hasPanel = false;
  @state() private _iconVariant: IconVariant = 'none';
  private _isDesktop = false;

  private readonly _uid = generateUid();
  private _ro: ResizeObserver | undefined;
  private _navbarHost: HTMLElement | undefined;

  @query('.panel') private _panelEl!: HTMLElement;
  @query('.trigger') private _triggerEl!: HTMLElement;

  set navbarHost(value: HTMLElement | null | undefined) {
    this._navbarHost = value ?? undefined;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    const target = document.documentElement;

    // Synchronous init — ResizeObserver callbacks are async (post-layout
    // microtask) so without this the first render always sees _isDesktop=true
    // and [mobile] is never set, leaving triggers at their desktop width.
    this._syncLayoutMode();

    this._ro = new ResizeObserver(() => {
      const layoutChanged = this._syncLayoutMode();
      if (layoutChanged) this.requestUpdate();
      if (this.open && this._isDesktop) this._positionDropdown();
    });
    this._ro.observe(target);
    window.addEventListener('resize', this._onWindowResize);
    window.addEventListener('scroll', this._onScroll, { capture: true, passive: true });
  }

  override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('inNavbar') || changed.has('mobile')) this._syncLayoutMode();
  }

  override updated(changed: PropertyValues): void {
    if (
      !changed.has('_hasPanel')
      && !changed.has('open')
      && !changed.has('inNavbar')
      && !changed.has('mobile')
    ) return;
    if (!this._panelEl) return;

    const supportsPopover = this._supportsPopoverApi();
    const usePopover = this._hasPanel && this._isDesktop && supportsPopover;
    const hasPopover = this._panelEl.hasAttribute('popover');

    // ── Wire / unwire popover attribute ──────────────────────────────────────
    // All desktop items with panels use the Popover API so the panel escapes
    // shadow DOM to the top layer. Mobile items never get popover.
    if (usePopover && !hasPopover) {
      this._panelEl.setAttribute('popover', 'auto');
      this._panelEl.addEventListener('toggle', this._handlePanelToggle);
    } else if (!usePopover && hasPopover) {
      try { this._panelEl.hidePopover(); } catch { /* already hidden */ }
      this._panelEl.removeAttribute('popover');
      this._panelEl.removeEventListener('toggle', this._handlePanelToggle);
      if (this.open) this.open = false;
    }

    // ── Sync open state when open=true arrives before popover was wired ───────
    // Also handles Storybook controls and programmatic el.open = true.
    if (usePopover && this._panelEl.hasAttribute('popover')) {
      if (this.open) {
        try { this._panelEl.showPopover(); } catch { /* already showing */ }
        this._positionDropdown();
      } else {
        try { this._panelEl.hidePopover(); } catch { /* already hidden */ }
        this._clearDropdownPosition();
      }
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._ro?.disconnect();
    this._panelEl?.removeEventListener('toggle', this._handlePanelToggle);
    window.removeEventListener('resize', this._onWindowResize);
    window.removeEventListener('scroll', this._onScroll, { capture: true });
  }

  private _onPanelSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasPanel = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _onIconSlotChange(): void {
    const iconSlot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="icon"]');
    this._iconVariant = (iconSlot?.assignedNodes({ flatten: true }).length ?? 0) > 0 ? 'small' : 'none';
  }

  /**
   * Positions the popover panel using getBoundingClientRect() so coordinates
   * are always relative to the current viewport state.
   * - Inside asc-navbar: full-width strip flush with the bottom of the bar.
   * - Standalone: anchored directly below the trigger button.
   */
  private _positionDropdown(): void {
    const s = this._panelEl.style;
    s.position = 'fixed';
    s.bottom = 'auto';
    if (this.inNavbar) {
      const navbar = this._navbarHost;
      // For floating-2-col, use the inner .container (the pill) so the panel
      // matches the pill width rather than the full-width host element.
      const anchorEl = this.type === 'floating-2-col'
        ? navbar?.shadowRoot?.querySelector<HTMLElement>('.container') ?? navbar
        : navbar;
      const r = anchorEl?.getBoundingClientRect();
      if (!r) return;
      const gap = this.type === 'floating-2-col' ? 4 : 0;
      s.top = `${r.bottom + gap}px`;
      s.left = `${r.left}px`;
      s.right = `${document.documentElement.clientWidth - r.right}px`;
      if (this.type === 'floating-2-col') this._panelEl.setAttribute('data-mode', 'floating');
      else this._panelEl.removeAttribute('data-mode');
      s.width = 'auto';
      s.minWidth = '';
    } else {
      // Standalone: spans the parent container, positioned below the trigger.
      const triggerRect = this._triggerEl.getBoundingClientRect();
      const container = (this.parentElement ?? document.body) as HTMLElement;
      const containerRect = container.getBoundingClientRect();
      s.top = `${triggerRect.bottom}px`;
      s.left = `${containerRect.left}px`;
      s.right = `${document.documentElement.clientWidth - containerRect.right}px`;
      s.width = 'auto';
      s.minWidth = '';
      this._panelEl.removeAttribute('data-mode');
    }
  }

  private _clearDropdownPosition(): void {
    const s = this._panelEl.style;
    s.position = '';
    s.top = '';
    s.left = '';
    s.right = '';
    s.width = '';
    s.minWidth = '';
    s.bottom = '';
    this._panelEl.removeAttribute('data-mode');
  }

  private _onWindowResize = (): void => {
    if (this.open && this._isDesktop) this._positionDropdown();
  };

  private _onScroll = (): void => {
    if (this.open && this._isDesktop) this._positionDropdown();
  };

  private _handleTriggerClick(): void {
    if (!this._hasPanel) {
      dispatch(this, 'asc-click');
      return;
    }
    if (this._isDesktop) {
      const hasPopoverApi = this._supportsPopoverApi();
      if (!hasPopoverApi) {
        const next = !this.open;
        this.open = next;
        dispatch<{ open: boolean }>(this, 'asc-toggle', { detail: { open: next } });
        return;
      }
      // Desktop: delegate open/close to Popover API.
      if (this.open) {
        this._panelEl.hidePopover();
      } else {
        this._panelEl.showPopover();
        this._positionDropdown();
      }
    } else {
      // Mobile accordion.
      const next = !this.open;
      this.open = next;
      dispatch<{ open: boolean }>(this, 'asc-toggle', { detail: { open: next } });
    }
  }

  private _handlePanelToggle = (e: Event): void => {
    const toggleEvent = e as ToggleEvent;
    const next = toggleEvent.newState === 'open';
    this.open = next;
    if (!next) this._clearDropdownPosition();
    dispatch<{ open: boolean }>(this, 'asc-toggle', { detail: { open: next } });
  };

  private _handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this._isDesktop && this.open) {
      if (this._supportsPopoverApi()) this._panelEl.hidePopover();
      this.shadowRoot?.querySelector<HTMLButtonElement>('.trigger')?.focus();
    }
  }

  override focus(options?: FocusOptions): void {
    if (this._triggerEl) {
      this._triggerEl.focus(options);
      return;
    }
    super.focus(options);
  }

  private _supportsPopoverApi(): boolean {
    const panel = this._panelEl as HTMLElement & {
      showPopover?: () => void;
      hidePopover?: () => void;
    };
    return typeof panel.showPopover === 'function' && typeof panel.hidePopover === 'function';
  }

  private _syncLayoutMode(): boolean {
    const wasDesktop = this._isDesktop;
    if (this.inNavbar) {
      this._isDesktop = !this.mobile;
      this.toggleAttribute('standalone', false);
      return wasDesktop !== this._isDesktop;
    }
    this._isDesktop = document.documentElement.offsetWidth >= MOBILE_BREAKPOINT_PX;
    this.toggleAttribute('standalone', this._isDesktop);
    return wasDesktop !== this._isDesktop;
  }

  override render() {
    const triggerId = `trigger-${this._uid}`;
    const panelId = `panel-${this._uid}`;

    const isLink = !!this.href && !this._hasPanel;
    const computedRel = isLink && this.target === '_blank' && this.rel == null
      ? 'noopener noreferrer'
      : this.rel;

    const triggerInner = html`
      <span
        part="icon-wrapper"
        class="icon-wrapper"
        aria-hidden="true"
        ?hidden="${this._iconVariant !== 'small'}"
      >
        <slot name="icon" @slotchange="${this._onIconSlotChange}"></slot>
      </span>
      <span part="label" class="label"><slot></slot></span>
      ${this._hasPanel
        ? html`
            <svg part="chevron" class="chevron" aria-hidden="true" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.312083 4.66462C0.22875 4.66462 0.15 4.63538 0.09125 4.58212C-0.0304167 4.47262 -0.0304167 4.29413 0.09125 4.18425L4.55792 0.165C4.67583 0.0585 4.8325 0 4.99917 0C5.16625 0 5.32292 0.0585001 5.44083 0.16425L9.90792 4.18425C10.0296 4.29375 10.0296 4.47225 9.90792 4.58212C9.84875 4.63538 9.77042 4.66462 9.68708 4.66462C9.60375 4.66462 9.525 4.63538 9.46625 4.58212L4.99958 0.5625L0.532917 4.58212C0.474167 4.63538 0.395417 4.66462 0.312083 4.66462Z" fill="currentColor"/>
            </svg>
          `
        : nothing}
    `;

    const trigger = isLink
      ? html`<a
          part="trigger"
          class="trigger"
          id="${triggerId}"
          href="${this.href!}"
          target="${this.target ?? nothing}"
          rel="${computedRel ?? nothing}"
          aria-label="${this.label ?? nothing}"
          @click="${this._handleTriggerClick}"
        >${triggerInner}</a>`
      : html`<button
          part="trigger"
          class="trigger"
          type="button"
          id="${triggerId}"
          aria-haspopup="${this._hasPanel ? 'true' : nothing}"
          aria-expanded="${this._hasPanel ? String(this.open) : nothing}"
          aria-controls="${this._hasPanel ? panelId : nothing}"
          aria-label="${this.label ?? nothing}"
          @click="${this._handleTriggerClick}"
        >${triggerInner}</button>`;

    return html`
      <div part="base" class="base" @keydown="${this._handleKeydown}">
        ${trigger}

        <div
          part="panel"
          class="panel"
          id="${panelId}"
          role="region"
          aria-labelledby="${triggerId}"
          ?inert="${!this.open && !this._isDesktop}"
        >
          <div class="panel-inner">
            <slot name="panel" @slotchange="${this._onPanelSlotChange}"></slot>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-nav-item': AscNavItem;
  }
}
